<template>
  <li :id="'lesson-row-' + lesson.uuid"
      class="group flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3 transition-colors duration-150 motion-reduce:transition-none hover:bg-gray-50 sm:flex-nowrap sm:px-4">
    <template v-if="features.reorder">
      <reorder-buttons-component
        :can-move-up="canMoveUp" :can-move-down="canMoveDown"
        :up-label="$t('projects.move_lesson_up')" :down-label="$t('projects.move_lesson_down')"
        :disabled="savingOrder"
        @move-up="$emit('move', -1)" @move-down="$emit('move', 1)"/>
      <span class="drag-handle flex h-11 w-8 flex-shrink-0 cursor-grab items-center justify-center rounded-md text-gray-500 hover:text-gray-700 active:cursor-grabbing md:h-auto md:w-auto md:p-1"
            :title="$t('projects.drag_lesson')" aria-hidden="true">
        <font-awesome-icon icon="grip-vertical" class="h-4 w-4"/>
      </span>
    </template>

    <div class="min-w-0 flex-1">
      <template v-if="features.renameLesson">
        <!-- Clic para editar (FE-064): fuera de edición el título se lee entero, también en móvil. -->
        <button v-if="!editing" ref="titleDisplay" :id="'lesson-title-display-' + lesson.uuid" type="button" v-on:click="startEditing" :disabled="renaming"
                :aria-label="$t('projects.rename_lesson', { title: lesson.title })"
                class="block w-full break-words rounded-md px-2 py-1 text-left text-sm font-medium text-gray-900 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue disabled:opacity-60">
          {{ title }}
        </button>
        <label v-if="editing" :for="'lesson-title-' + lesson.uuid" class="sr-only">{{ $t('projects.lesson_title_label') }}</label>
        <!-- Edición en línea: Enter o salir del campo guardan si cambió; Escape deshace. -->
        <input v-if="editing" ref="titleInput" :id="'lesson-title-' + lesson.uuid" v-model="title" type="text" :disabled="renaming"
               @keydown.enter.prevent="$event.target.blur()" @keydown.esc.prevent="revertTitle" @blur="commitTitle"
               :title="$t('projects.lesson_title_edit_hint')"
               class="block w-full rounded-md border-transparent bg-transparent px-2 py-1 text-sm font-medium text-gray-900 hover:border-gray-300 hover:bg-white focus:border-bpblue focus:bg-white focus:ring-bpblue disabled:opacity-60">
      </template>
      <router-link v-else :id="'lesson-title-display-' + lesson.uuid" :to="editRoute"
                   class="block truncate rounded-md text-sm font-medium text-gray-900 hover:text-bpblue focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
        {{ lesson.title }}
      </router-link>
      <div class="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-600" :class="{'px-2': features.renameLesson}">
        <span>{{ typeLabel }}</span>
        <span v-if="features.freePreview && lesson.is_free_preview" class="rounded-md bg-blue-50 px-2 py-0.5 font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
          {{ $t('projects.free_preview_badge') }}
        </span>
        <span v-if="justSaved" class="inline-flex items-center gap-1 font-medium text-green-700" aria-hidden="true">
          <font-awesome-icon icon="check" class="h-3 w-3"/>
          {{ $t('projects.saved_inline') }}
        </span>
      </div>
    </div>

    <div class="flex w-full flex-shrink-0 items-center justify-end gap-2 sm:w-auto">
      <action-menu-component v-if="statusMenuItems.length" :items="statusMenuItems" icon="" :icon-only="false"
                             :label="statusLabel" :trigger-class="statusClass" @select="onStatusAction"></action-menu-component>
      <router-link :to="editRoute" :aria-label="$t('projects.edit_lesson_content_of', { title: lesson.title })"
                   class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-white hover:text-bpblue focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
        <font-awesome-icon icon="pen" class="h-3.5 w-3.5" aria-hidden="true"/>
        <span>{{ $t('projects.edit_content') }}</span>
      </router-link>
      <action-menu-component v-if="moreMenuItems.length" :items="moreMenuItems" :trigger-class="touchMenuTriggerClass"
                             :label="$t('projects.lesson_actions', { title: lesson.title })"
                             @select="onMoreAction"></action-menu-component>
    </div>
  </li>
</template>

<script>
import ActionMenuComponent from "@/components/menus/ActionMenuComponent";
import ReorderButtonsComponent from "@/views/projects/ProjectEdit/Curriculum/ReorderButtonsComponent";

/*
 * Fila de una lección en el currículo (FE-063): título editable en línea, estado
 * como píldora con menú, «Editar contenido» y menú «⋯». No llama a la API: emite
 * y el currículo decide (store + feedback sin toasts).
 */
export default {
  name: "LessonRowComponent",
  components: {
    ActionMenuComponent,
    ReorderButtonsComponent
  },
  props: {
    lesson: {
      type: Object,
      required: true
    },
    savedTitle: {
      type: String,
      default: ''
    },
    typeLabel: {
      type: String,
      default: ''
    },
    features: {
      type: Object,
      required: true
    },
    canMoveUp: {
      type: Boolean,
      default: false
    },
    canMoveDown: {
      type: Boolean,
      default: false
    },
    savingOrder: {
      type: Boolean,
      default: false
    },
    renaming: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    },
    justSaved: {
      type: Boolean,
      default: false
    }
  },
  emits: ['move', 'rename', 'set-published', 'set-free-preview', 'duplicate', 'delete'],
  data() {
    return {
      title: this.lesson.title,
      editing: false,
    }
  },
  computed: {
    editRoute() {
      return {name: 'projects-edit-content', params: {uuid: this.lesson.uuid}}
    },
    isPublished() {
      return !!this.lesson.published_at
    },
    statusLabel() {
      return this.$t(this.isPublished ? 'projects.status_published' : 'projects.status_draft')
    },
    statusClass() {
      const tone = this.isPublished
          ? 'bg-green-50 text-green-700 ring-green-600/20 hover:bg-green-100'
          : 'bg-gray-100 text-gray-700 ring-gray-500/20 hover:bg-gray-200'
      return 'inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue ' + tone
    },
    statusMenuItems() {
      const items = []
      if (this.features.publish) {
        items.push(this.isPublished
            ? {key: 'unpublish', label: this.$t('projects.unpublish_lesson'), icon: 'eye-slash'}
            : {key: 'publish', label: this.$t('projects.publish_lesson'), icon: 'eye'})
      }
      if (this.features.freePreview) {
        items.push(this.lesson.is_free_preview
            ? {key: 'preview-off', label: this.$t('projects.free_preview_disable'), icon: 'lock'}
            : {key: 'preview-on', label: this.$t('projects.free_preview_enable'), icon: 'unlock',
              description: this.$t('projects.free_preview_enable_help')})
      }
      return items
    },
    // Botón «⋯» con área táctil de 44 px en móvil (FE-064).
    touchMenuTriggerClass() {
      return 'inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue md:h-8 md:w-8'
    },
    moreMenuItems() {
      return [
        {key: 'duplicate', label: this.$t('projects.duplicate_lesson'), icon: 'copy', disabled: this.busy,
          description: this.$t('projects.duplicate_lesson_help')},
        ...(this.features.deleteLesson
            ? [{key: 'delete', label: this.$t('projects.delete_lesson'), icon: 'trash', danger: true, disabled: this.busy}]
            : []),
      ]
    }
  },
  watch: {
    // El título puede cambiar desde fuera (recarga del proyecto, error al guardar).
    'lesson.title'(title) {
      this.title = title
    }
  },
  methods: {
    startEditing() {
      this.editing = true
      this.$nextTick(() => this.$refs.titleInput?.focus())
    },
    commitTitle() {
      this.editing = false
      const title = this.title.trim()
      if (!title) {
        this.title = this.savedTitle
        return
      }
      if (title !== this.savedTitle) this.$emit('rename', title)
    },
    // Escape: deshace y devuelve el foco al título, sin guardar.
    revertTitle() {
      this.title = this.savedTitle
      this.editing = false
      this.$nextTick(() => this.$refs.titleDisplay?.focus())
    },
    onStatusAction(action) {
      if (action === 'publish' || action === 'unpublish') this.$emit('set-published', action === 'publish')
      if (action === 'preview-on' || action === 'preview-off') this.$emit('set-free-preview', action === 'preview-on')
    },
    onMoreAction(action) {
      if (action === 'duplicate') this.$emit('duplicate')
      if (action === 'delete') this.$emit('delete')
    }
  }
}
</script>
